import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
  type FC,
  type CSSProperties,
} from 'react';
import { FluentProvider } from '@fluentui/react-components';
import {
  type ThemeMode,
  getThemeForMode,
  getStoredThemeMode,
  setStoredThemeMode,
} from './customTheme';
import { APP_FONT_FAMILY } from './scalingTokens';
import { AppStyles } from './AppStyles';

interface ThemeContextValue {
  /** Current theme mode */
  mode: ThemeMode;
  /** Toggle between light and dark themes */
  toggleTheme: () => void;
  /** Set a specific theme mode */
  setTheme: (mode: ThemeMode) => void;
  /** Whether the current theme is dark mode */
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  /** Child components */
  children?: ReactNode;
  /** Initial theme mode (defaults to stored preference or 'light') */
  defaultMode?: ThemeMode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children, defaultMode }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = getStoredThemeMode();
    return stored ?? defaultMode ?? 'light';
  });

  useEffect(() => {
    setStoredThemeMode(mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setTheme = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
  }, []);

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme,
    setTheme,
    isDarkMode: mode === 'dark',
  };

  const theme = getThemeForMode(mode);
  const providerStyle = {
    fontFamily: APP_FONT_FAMILY,
    ...(mode === 'dark'
      ? {
          '--gem-status-new-request-background': theme.colorPaletteYellowBackground1,
          '--gem-status-new-request-border': theme.colorPaletteYellowBorder1,
          '--gem-status-new-request-foreground': theme.colorPaletteYellowForeground1,
          '--gem-status-under-review-background': theme.colorPaletteMarigoldBackground1,
          '--gem-status-under-review-border': theme.colorPaletteMarigoldBorder1,
          '--gem-status-under-review-foreground': theme.colorPaletteMarigoldForeground2,
          '--gem-calendar-confirmed-foreground': theme.colorNeutralForeground1,
        }
      : {}),
  } as CSSProperties;

  return (
    <ThemeContext.Provider value={contextValue}>
      <FluentProvider theme={theme} style={providerStyle}>
        <AppStyles />
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

export default ThemeProvider;
